/**
 * Which app does this person belong in?
 *
 * On a per-company address the answer is baked into the bundle: HR Admin is
 * `admin`, Employee Web is `mobile`. The global login has no such luxury — it
 * gets an email and a password and nothing else, and only the backend knows
 * which surfaces the account may use.
 *
 * So we ask, by trying. `admin` first, because the refusal is explicit and
 * arrives EARLY: `POST /auth/login` answers `403
 * AUTH_ADMIN_AUDIENCE_NOT_PERMITTED` before any OTP/TOTP step, so a refused
 * attempt leaves nothing half-started and the same credentials go straight
 * back out as `mobile`. One extra request, in the less common case, and the
 * user sees a single pending state rather than a second form.
 *
 * `sessions:exchange` is NOT used to probe: trying `admin` IS the probe, and
 * its refusal is a localized answer from the only authority on the question.
 */
import { appBasePathForAudience, type Audience } from "@linkhr/auth";

/** What the global login tries first. */
export const FIRST_AUDIENCE: Audience = "admin";

/**
 * The backend refused `attempted`. What now?
 *
 * `admin` refused → the account is employee-only; retry as `mobile`.
 * Anything else refused → we are out of surfaces; show the backend's own
 * refusal rather than looping.
 */
export function nextAudienceAfterRefusal(attempted: Audience): Audience | null {
  return attempted === "admin" ? "mobile" : null;
}

/**
 * The same question for a provider's ID token (Google, Microsoft): may we send
 * it again as `mobile`?
 *
 * Yes — for both, the backend accepts one ID token more than once until the
 * provider's own expiry (no replay/nonce check; both calls share login's rate
 * budget), so a provider token follows the password probe exactly. If that
 * ever changes, this is the one place to say `null`: the admin refusal then
 * shows as it does today. If the second call is refused (401), the form shows
 * the provider's generic message; `mobile` refused → `null`, so there is never
 * a third call.
 */
export function nextAudienceForIdToken(attempted: Audience): Audience | null {
  return nextAudienceAfterRefusal(attempted);
}

/**
 * Where a signed-in session lands. Same origin as the global login, so this is
 * a path, not a host — switching apps never leaves `app.…`.
 */
export function landingPath(aud: Audience): string {
  return appBasePathForAudience(aud);
}
